import { useMemo } from "react";
import { classNames } from "helpers";
import { useTranslate } from "helpers/common";
import { Loader } from "ui/components/Loader";
import { buildTimelineColumns } from "ui/modules/notifications/timeline";
import {
  DEFAULT_TIMELINE_BAR_COLOR,
  TIMELINE_BAR_COLORS,
  WEEKDAY_SHORT_NAMES,
} from "./consts";
import TimelineRow from "./TimelineRow";
import { TimelineColumnModel, VehicleTimelineProps } from "./types";

const VehicleTimeline: React.FC<VehicleTimelineProps> = (props) => {
  const { groups, isLoading, from, to, onVehicleClick } = props;

  const { t } = useTranslate();

  const columns = useMemo<TimelineColumnModel[]>(() => {
    const today = new Date();

    // Stupci su uvijek dani; raspon (mjesec ili tjedan) bira alatna traka.
    return buildTimelineColumns(from, to, "day").map((column) => ({
      ...column,
      isToday: today >= column.start && today <= column.end,
      isWeekend: column.start.getDay() === 0 || column.start.getDay() === 6,
    }));
  }, [from, to]);

  const usedEntityTypes = useMemo(() => {
    const types = new Set<string>();
    groups.forEach((group) => group.items.forEach((item) => types.add(item.entityType)));

    return Array.from(types);
  }, [groups]);

  if (isLoading) {
    return (
      <div className="py-16">
        <Loader isLoading />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {t("admin.notification.timeline.empty")}
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Zaglavlje stupaca */}
          <div className="flex border-b border-slate-200 bg-slate-50">
            <div className="w-[260px] shrink-0 px-4 py-2 border-r border-slate-200 text-xs font-medium text-gray-500">
              {t("admin.notification.timeline.vehicle")}
            </div>
            <div
              className="flex-1 grid"
              style={{
                gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
              }}
            >
              {columns.map((column, index) => (
                <div
                  key={index}
                  className={classNames(
                    "py-2 text-center border-r border-slate-200 last:border-r-0",
                    column.isToday ? "bg-sky-100" : ""
                  )}
                >
                  <div
                    className={classNames(
                      "text-xs font-semibold",
                      column.isToday ? "text-sky-700" : "text-gray-700"
                    )}
                  >
                    {column.label}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {WEEKDAY_SHORT_NAMES[column.start.getDay()]}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {groups.map((group) => (
            <TimelineRow
              key={group.vehicle.id}
              group={group}
              columns={columns}
              onVehicleClick={onVehicleClick}
            />
          ))}
        </div>
      </div>

      {usedEntityTypes.length > 0 && (
        <div className="flex flex-wrap items-center gap-4 px-6 py-3 border-t border-slate-100">
          {usedEntityTypes.map((entityType) => (
            <div key={entityType} className="flex items-center gap-2">
              <span
                className={classNames(
                  "h-2.5 w-2.5 rounded-full",
                  TIMELINE_BAR_COLORS[entityType] ?? DEFAULT_TIMELINE_BAR_COLOR
                )}
              />
              <span className="text-xs text-gray-600">
                {t(`admin.notification.entityType.${entityType}`)}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default VehicleTimeline;
